import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PetType } from '@spike/model';
import { Room } from '@spike/room-model';
import { fetchOrganizationServicesInfoThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { OverFullWindow, Spinner } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import { useMasterData, useMarketplace, useApiClientWrapper } from 'hooks';
import {
    createEmptyBoardingService,
    Service,
    ServicePricing,
    FixedPriceOption,
    BOARDING_DEFAULTS,
    LateCheckoutFeeType,
    MetadataDuration,
    BoardingMetadata,
    isMetadataDuration,
    LATE_CHECKOUT_FEE_TYPES
} from 'model/Service';
import { timeStringToInteger, integerTimeToString } from 'utils/TimeOptionsUtils';
import { saveBulkServicesThunk, fetchServicesThunk } from 'actions/services/ServicesActions';
import { RootState } from 'store';
import styles from './BoardingForm.module.scss';
import { BoardingPetTypeStep } from './Steps/PetType/BoardingPetTypeStep';
import { BoardingDetailsStep } from './Steps/Details/BoardingDetailsStep';
// Types for Boarding Form
export type TimeMode = '24hours' | 'setTimes';

interface TimeRange {
    from: string;
    to: string;
}
import { BoardingPricingStep } from './Steps/Pricing/BoardingPricingStep';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import useNonInitialEffect from '@versiondos/hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';

// Props for both add and edit modes
interface BoardingFormProps {
    mode: 'add' | 'edit';
    service?: Service; // Required for edit mode
    rooms: Room[];
    roomsLoading: boolean;
    onClose: () => void;
}

// Local interface for rooms (no longer needed since we receive rooms as props)

export const BoardingForm: React.FC<BoardingFormProps> = props => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const fetchPendingRef = useRef(false);

    // Use global RootState for services
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    // Use rooms from props instead of fetching
    const { rooms, roomsLoading } = props;
    const [formStep, setFormStep] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOrganizationServices, setIsLoadingOrganizationServices] = useState(true);
    const [isEditingSpecificStep, setIsEditingSpecificStep] = useState(false);

    // Conditional based on mode
    const [boardingService, setBoardingService] = useState<Service>(() => {
        if (props.mode === 'edit' && props.service) {
            return props.service;
        }
        return createEmptyBoardingService(masterData, marketplace);
    });

    const [showExitPrompt, setShowExitPrompt] = useState(false);

    // Load organization services info and related services data on mount
    useEffect(() => {
        setIsLoadingOrganizationServices(true);
        fetchPendingRef.current = false;
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        dispatch(
            fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'grooming,dental')
        );
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'daycare'));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchingOrganizationServicesInfo) {
            fetchPendingRef.current = true;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.FetchOrganizationServicesInfoSuccess) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.Error) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }
    }, [marketplaceStatus]);

    // Handle services status changes
    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.SaveSuccess:
            case ServicesStatus.SaveBulkSuccess:
                setIsLoading(false);
                setIsSuccess(true);
                break;
            case ServicesStatus.Error:
                setIsLoading(false);
                setIsSuccess(false);
                break;
        }
    }, [servicesStatus]);

    // Get available rooms from props (already filtered for boarding room type)
    const availableRooms = useMemo(() => {
        return rooms || [];
    }, [rooms]);

    // Get available rooms filtered by selected pet types
    const availableRoomsFiltered = useMemo(() => {
        if (!availableRooms.length || !boardingService.petTypes?.length) return availableRooms;

        const selectedPetTypeIds = boardingService.petTypes.map(pt => pt.id);

        // Filter rooms that support ALL of the selected pet types
        const compatibleRooms = availableRooms.filter(room => {
            if (!room.petTypes?.length) return false;
            const roomPetTypeIds = room.petTypes.map(pt => pt.id);
            return selectedPetTypeIds.every(selectedId => roomPetTypeIds.includes(selectedId));
        });

        return compatibleRooms;
    }, [availableRooms, boardingService.petTypes]);

    //Auto sync pricing when pet types change
    useEffect(() => {
        if (!boardingService.petTypes || !boardingService.pricing?.fixedPrice) return;

        const petTypeIds = boardingService.petTypes.map(pt => pt.id);
        const currentPricing = boardingService.pricing.fixedPrice;

        // Find pet types that need pricing
        const missingPetTypes = boardingService.petTypes.filter(
            pt => !currentPricing.some(fp => fp.petType.id === pt.id)
        );

        // Remove pricing of pet types that are no longer selected
        const validPricing = currentPricing.filter(fp => petTypeIds.includes(fp.petType.id));

        // If there are changes, auto-sync
        if (missingPetTypes.length > 0 || validPricing.length !== currentPricing.length) {
            const newPricing: FixedPriceOption[] = [
                ...validPricing,
                ...missingPetTypes.map(petType => ({
                    petType,
                    price: 0
                }))
            ];

            setBoardingService(prev => ({
                ...prev,
                pricing: {
                    ...prev.pricing!,
                    fixedPrice: newPricing
                }
            }));
        }
    }, [boardingService.petTypes]);

    // Adaptive strings based on mode
    const getTitle = () => {
        return props.mode === 'edit' ? `Editing ${boardingService.name}` : 'Adding a Boarding Service';
    };

    const getSuccessTitle = () => {
        return props.mode === 'edit' ? 'Updated Successfully' : 'Good Job';
    };

    const getSuccessMessage = () => {
        const action = props.mode === 'edit' ? 'updated' : 'added';
        return (
            <p className={styles.successDescription}>
                <b>{boardingService.name}</b> was {action} successfully.
            </p>
        );
    };

    const getSuccessButtonLabel = () => {
        return 'Go to Boarding Services';
    };

    // Extract current times for edit mode
    const getCurrentCheckInTime = () => {
        if (boardingService.checkInTime) {
            const result = {
                from: integerTimeToString(boardingService.checkInTime.start, '12h'),
                to: integerTimeToString(boardingService.checkInTime.end, '12h')
            };
            return result;
        }
        return BOARDING_DEFAULTS.CHECK_IN_TIME;
    };

    const getCurrentCheckOutTime = () => {
        if (boardingService.checkOutTime) {
            const result = {
                from: integerTimeToString(boardingService.checkOutTime.start, '12h'),
                to: integerTimeToString(boardingService.checkOutTime.end, '12h')
            };
            return result;
        }
        return BOARDING_DEFAULTS.CHECK_OUT_TIME;
    };

    const nextStepHandler = () => setFormStep(formStep + 1);

    const editStepHandler = (step: number) => {
        setFormStep(step);
        setIsEditingSpecificStep(true);
    };

    const handlePetTypeStep = (petTypes: Array<PetType>, marketplaces: Service['marketplaces']) => {
        setBoardingService({
            ...boardingService,
            petTypes,
            marketplaces
        });
        nextStepHandler();
    };

    const handleDetailsStep = (details: {
        name: string;
        description: string;
        checkInTime: TimeRange;
        checkOutTime: TimeRange;
        timeMode: TimeMode;
        color?: string;
        onlineBooking: boolean;
        forms?: import('model/Forms').AssociatedForm[];
        selectedRooms: Array<string>;
        marketplaces: Service['marketplaces'];
        dropoff24h?: boolean;
        minNights?: number;
    }) => {
        // Preserve existing boarding metadata
        const existingBoardingData = boardingService.boardingData;
        const updatedMetadata = {
            dropoff_24h: details.timeMode === '24hours' || details.dropoff24h || false,
            rooms_enabled: true, // Always enabled for Boarding
            selected_rooms: details.selectedRooms || [],
            min_nights: details.minNights || 1,
            late_pickup_fee: {
                enabled: existingBoardingData?.latePickupFee?.enabled || false,
                type: existingBoardingData?.latePickupFee?.type || LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                grace_period: existingBoardingData?.latePickupFee?.gracePeriod || 30,
                fees: existingBoardingData?.latePickupFee?.fees || {}
            }
        };

        // Only set specific times if timeMode is 'setTimes'
        const newCheckInTime =
            details.timeMode === 'setTimes'
                ? {
                      start: timeStringToInteger(details.checkInTime.from),
                      end: timeStringToInteger(details.checkInTime.to)
                  }
                : undefined;
        const newCheckOutTime =
            details.timeMode === 'setTimes'
                ? {
                      start: timeStringToInteger(details.checkOutTime.from),
                      end: timeStringToInteger(details.checkOutTime.to)
                  }
                : undefined;

        setBoardingService({
            ...boardingService,
            name: details.name,
            description: details.description,
            marketplaces: details.marketplaces,
            checkInTime: newCheckInTime,
            checkOutTime: newCheckOutTime,
            color: details.color || '',
            olbEnabled: details.onlineBooking,
            forms: details.forms || [],
            // Boarding should not include bundled items.
            serviceItems: [],
            // Update parsed boardingData for UI consistency
            boardingData: {
                dropoff24h: details.timeMode === '24hours' || details.dropoff24h || false,
                roomsEnabled: true, // Always enabled for Boarding
                selectedRooms: details.selectedRooms || [],
                minNights: details.minNights || 1,
                latePickupFee: existingBoardingData?.latePickupFee || {
                    enabled: false,
                    type: LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                    gracePeriod: 30,
                    fees: {}
                }
            } as BoardingMetadata,
            // Update raw metadata for backend compatibility
            duration: {
                metadata: JSON.stringify(updatedMetadata)
            } as MetadataDuration
        });
        nextStepHandler();
    };

    const handlePricingStep = async (
        pricing: ServicePricing,
        boardingData: {
            dropoff24h: boolean;
            minNights: number;
            latePickupFee: {
                enabled: boolean;
                type?: LateCheckoutFeeType;
                gracePeriod: number;
                fees: Record<string, number>;
            };
        },
        marketplaces: Service['marketplaces']
    ) => {
        try {
            setIsLoading(true);
            // Parse metadata set in the Details step (source of truth for dropoff_24h and rooms)
            let currentDropoff24h = boardingService.boardingData?.dropoff24h ?? boardingData.dropoff24h ?? false;
            let currentRoomsEnabled = false;
            let currentSelectedRooms: string[] = [];

            if (isMetadataDuration(boardingService.duration) && boardingService.duration.metadata) {
                try {
                    const currentMetadata = JSON.parse(boardingService.duration.metadata);
                    if (currentMetadata.dropoff_24h !== undefined) {
                        currentDropoff24h = currentMetadata.dropoff_24h;
                    }
                    currentRoomsEnabled = currentMetadata.rooms_enabled || false;
                    currentSelectedRooms = currentMetadata.selected_rooms || [];
                } catch (error) {
                    //console.warn('Error parsing current metadata:', error);
                }
            }

            const boardingMetadata = {
                dropoff_24h: currentDropoff24h,
                rooms_enabled: currentRoomsEnabled,
                selected_rooms: currentSelectedRooms,
                min_nights: boardingData.minNights,
                late_pickup_fee: {
                    enabled: boardingData.latePickupFee.enabled,
                    type: boardingData.latePickupFee.type || LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                    grace_period: boardingData.latePickupFee.gracePeriod,
                    fees: boardingData.latePickupFee.fees
                }
            };

            // Update service with final data like regular services do
            const finalBoardingService: Service = {
                ...boardingService,
                marketplaces,
                serviceItems: [],
                pricing,
                duration: {
                    metadata: JSON.stringify(boardingMetadata)
                } as MetadataDuration
            };

            // Same bulk thunk used by grooming/daycare for both add and edit
            await dispatch(saveBulkServicesThunk(finalBoardingService));
        } catch (error) {
            // Show error notification
        } finally {
            setIsLoading(false);
        }
    };

    const getActiveStep = () => {
        if (props.mode === 'edit' && !isEditingSpecificStep) {
            return 3; // Show all steps completed only in initial state
        }
        return formStep;
    };

    // Memoize formData for BoardingDetailsStep
    const boardingDetailsFormData = useMemo(() => {
        const formData = {
            name: boardingService.name,
            description: boardingService.description,
            checkInTime: getCurrentCheckInTime(),
            checkOutTime: getCurrentCheckOutTime(),
            timeMode:
                boardingService.boardingData?.dropoff24h || !boardingService.checkInTime
                    ? ('24hours' as const)
                    : ('setTimes' as const),
            color: boardingService.color || undefined,
            onlineBooking: boardingService.olbEnabled,
            forms: boardingService.forms || [],
            // roomsEnabled always true for Boarding
            selectedRooms: boardingService.boardingData?.selectedRooms || [],
            dropoff24h: boardingService.boardingData?.dropoff24h || false,
            minNights: boardingService.boardingData?.minNights || 1
        };

        return formData;
    }, [
        boardingService.name,
        boardingService.description,
        boardingService.checkInTime,
        boardingService.checkOutTime,
        boardingService.boardingData?.dropoff24h,
        boardingService.color,
        boardingService.olbEnabled,
        boardingService.forms,
        boardingService.boardingData?.selectedRooms,
        boardingService.boardingData?.minNights
    ]);

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={getTitle()}
                onClose={props.onClose}
            >
                <SuccessScreen
                    title={getSuccessTitle()}
                    message={getSuccessMessage()}
                    buttonLabel={getSuccessButtonLabel()}
                    onClick={props.onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title={getTitle()}
            onClose={() => setShowExitPrompt(true)}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={() => props.onClose?.()}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            {isLoadingOrganizationServices ? (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            ) : (
                <div className={styles.container}>
                    <MultiStepForm
                        steps={[
                            {
                                title: 'Pet Type and Locations',
                                description: 'Select pet categories for this service',
                                component: (
                                    <BoardingPetTypeStep
                                        selectedPetTypes={boardingService.petTypes}
                                        marketplaces={boardingService.marketplaces}
                                        serviceCategory="boarding"
                                        serviceId={boardingService.id}
                                        onNext={handlePetTypeStep}
                                    />
                                )
                            },
                            {
                                title: 'Boarding Service Detail',
                                component: (
                                    <BoardingDetailsStep
                                        formData={boardingDetailsFormData}
                                        petTypes={boardingService.petTypes || []}
                                        marketplaces={boardingService.marketplaces}
                                        availableRooms={availableRoomsFiltered}
                                        roomsLoading={roomsLoading}
                                        serviceId={boardingService.id}
                                        onNext={handleDetailsStep}
                                    />
                                )
                            },
                            {
                                title: 'Pricing',
                                description: 'Choose a pricing option for this boarding service',
                                component: (
                                    <BoardingPricingStep
                                        selectedPetTypes={boardingService.petTypes}
                                        marketplaces={boardingService.marketplaces}
                                        existingService={boardingService}
                                        onNext={handlePricingStep}
                                    />
                                )
                            }
                        ]}
                        activeStep={getActiveStep()}
                        onEdit={editStepHandler}
                        flexibleNavigation={props.mode === 'edit'} //Enable flexible navigation only in edit mode
                    />
                </div>
            )}
        </OverFullWindow>
    );
};

export default BoardingForm;
